import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BookingForm from "../components/BookingForm";
import FAQAccordion from "../components/FAQAccordion";
import HeroVisual from "../components/HeroVisual";
import SectionHeading from "../components/SectionHeading";
import TutorCard from "../components/TutorCard";
import {
  faqItems,
  goalPrograms,
  howItWorks,
  liveClassHighlights,
  liveClasses,
  parentSignals,
  programPackages,
  siteInfo,
  stats,
  subjects,
  successStories,
} from "../data/content";
import { sampleTutors } from "../data/sampleTutors";
import { getTutors } from "../lib/storage";

const highlightBullets = [
  { icon: "⚡", text: "Free 30-minute diagnostic session" },
  { icon: "🧠", text: "Step-by-step mock test feedback" },
  { icon: "⏰", text: "Flexible timings and affordable batches" },
];

const subjectIcons = ["📐", "⚛", "🧪", "🧬", "🧭", "📝", "💹", "📊", "💻", "🌐"];

export default function Landing() {
  const navigate = useNavigate();
  const [featuredTutors, setFeaturedTutors] = useState(sampleTutors.slice(0, 6));
  const [heroSearch, setHeroSearch] = useState("");

  useEffect(() => {
    setFeaturedTutors(getTutors().slice(0, 6));
  }, []);

  const handleHeroSearch = (event) => {
    event.preventDefault();
    const query = heroSearch.trim();
    if (!query) {
      navigate("/marketplace");
      return;
    }
    navigate(`/marketplace?search=${encodeURIComponent(query)}`);
  };

  return (
    <div className="pb-10">
      <section className="section-shell pt-8 lg:pt-10">
        <div className="hero-stage grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div className="floating-dot -left-16 top-8 h-32 w-32" />
          <div className="floating-dot right-10 top-12 h-20 w-20" />
          <div className="floating-dot bottom-10 right-16 h-28 w-28" />
          <div className="relative z-10">
            <div className="eyebrow">Classes 7-12 | Boards | Competitive | Admission Support</div>
            <h1 className="headline-invert mt-5">
              Personalised One-to-One Tuition with Expert Verified Tutors
            </h1>
            <p className="mt-6 max-w-2xl text-xl leading-9 text-white/80">
              Master your syllabus with board-aligned lessons, live doubt support, timed mock
              tests, recorded sessions, and structured progress updates for parents.
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              {parentSignals.map((item) => (
                <span key={item} className="chip-dark">
                  {item}
                </span>
              ))}
            </div>

            <div className="mt-10 grid gap-3 sm:grid-cols-3">
              {highlightBullets.map((item, index) => (
                <div
                  key={item.text}
                  className="hero-stat text-sm font-semibold leading-6 text-white/82"
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  <span className="mr-2 text-lg">{item.icon}</span>
                  {item.text}
                </div>
              ))}
            </div>

            <form
              onSubmit={handleHeroSearch}
              className="mt-10 flex flex-col overflow-hidden rounded-[28px] border border-white/10 bg-white/10 backdrop-blur sm:flex-row"
            >
              <input
                type="search"
                value={heroSearch}
                onChange={(event) => setHeroSearch(event.target.value)}
                className="w-full bg-transparent px-6 py-5 text-base font-semibold text-white outline-none placeholder:text-white/45"
                placeholder="Search tutors, subjects, JEE, NEET, Spoken English..."
              />
              <button type="submit" className="button-primary rounded-none sm:rounded-l-none">
                Search Tutors
              </button>
            </form>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/marketplace" className="button-primary">
                Find Tutors
              </Link>
              <Link to="/#book-demo" className="button-ghost">
                Book Demo - Free
              </Link>
              <a href="tel:+919507505612" className="button-ghost">
                Call Counselor
              </a>
            </div>

            <div className="mt-10 grid gap-4 sm:grid-cols-3">
              {stats.map((stat, index) => (
                <div
                  key={stat.label}
                  className="hero-stat"
                  style={{ animationDelay: `${index * 0.25}s` }}
                >
                  <div className="font-display text-4xl font-extrabold text-white">{stat.value}</div>
                  <div className="mt-2 text-sm font-semibold text-white/72">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          <HeroVisual />
        </div>
      </section>

      <section id="book-demo" className="section-shell mt-12">
        <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr]">
          <div className="surface-panel p-9">
            <div className="eyebrow-light">Book Demo</div>
            <h2 className="section-title mt-4">Book a Free Demo</h2>
            <p className="mt-4 text-base leading-7 text-slate-600">
              Get a callback from our academic counselor within 24 hours to schedule a diagnostic.
              Every form submission is saved locally and pushed to WhatsApp for quick follow-up.
            </p>
            <div className="mt-8 grid gap-4">
              {[
                "Verified tutors matched to the learner's goal and class",
                "WhatsApp-first scheduling for parents",
                "Recorded sessions, notes, mocks, and reviews",
              ].map((item) => (
                <div key={item} className="soft-panel px-4 py-4 text-base font-semibold text-slate-700">
                  ✓ {item}
                </div>
              ))}
            </div>
          </div>
          <BookingForm compact={false} />
        </div>
      </section>

      <section id="goals" className="section-shell mt-24">
        <SectionHeading
          eyebrow="Targeted Preparation For Your Goals"
          title="Targeted Preparation For Your Goals"
          copy="Board exams, entrance prep, defence routes, language building, and practical skill tracks - all under one structured academic system."
        />
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {goalPrograms.map((item, index) => (
            <article key={item.title} className="surface-panel card-lift flex items-center gap-4 p-5">
              <div className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-navy-900 to-navy-700 text-2xl text-white shadow-neon">
                {item.symbol}
              </div>
              <p className="text-sm font-extrabold text-slate-700">{item.title}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="programs" className="section-shell mt-24">
        <div className="dark-shell p-8 lg:p-10">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <SectionHeading
              eyebrow="Programs & Packages"
              title="Programs & Packages"
              copy="Choose a plan based on your goal: board exams, competitive prep, spoken English, or computer skills. Flexible payment options are easy to explain to parents."
              invert
            />
            <Link to="/#book-demo" className="button-primary">
              Speak to Counselor
            </Link>
          </div>
          <div className="mt-10 grid gap-5 lg:grid-cols-4">
            {programPackages.map((program) => (
              <article key={program.title} className="glow-card p-6">
                {program.badge ? <div className="eyebrow">{program.badge}</div> : null}
                <h3 className="mt-4 font-display text-2xl font-extrabold text-white">{program.title}</h3>
                <p className="mt-4 text-3xl font-extrabold text-sun">{program.price}</p>
                <p className="mt-4 text-sm leading-7 text-white/72">{program.description}</p>
                <Link to="/#book-demo" className="button-ghost mt-6">
                  {program.action}
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-shell mt-24">
        <SectionHeading
          eyebrow="Subjects We Teach"
          title="Subjects We Teach"
          copy="Comprehensive coverage across all major streams, foundational school subjects, and practical skill courses."
        />
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {subjects.map((subject, index) => (
            <article key={subject.title} className="surface-panel card-lift p-5">
              <div className="info-pill">{subjectIcons[index]}</div>
              <h3 className="mt-4 font-display text-xl font-extrabold text-ink">{subject.title}</h3>
              <p className="mt-2 text-sm leading-7 text-slate-600">{subject.detail}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section-shell mt-24">
        <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="surface-panel p-8 lg:p-9">
            <SectionHeading
              eyebrow="Find Tutors - Marketplace"
              title="Find Tutors - Marketplace"
              copy="Search, filter, and compare verified tutors. View profiles, check prices, and book a demo instantly."
            />
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {[
                "Fast search and subject filters",
                "Tutor profile pages with reviews",
                "WhatsApp-ready demo booking",
                "Verified profiles with ratings",
              ].map((item) => (
                <div key={item} className="soft-panel px-4 py-4 text-base font-semibold text-slate-700">
                  ✓ {item}
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/marketplace" className="button-primary">
                Browse All Tutors
              </Link>
              <Link to="/#book-demo" className="button-secondary">
                Speak to Counselor
              </Link>
            </div>
          </div>

          <div className="grid gap-5 xl:grid-cols-2">
            {featuredTutors.slice(0, 4).map((tutor) => (
              <TutorCard key={tutor.id} tutor={tutor} />
            ))}
          </div>
        </div>
      </section>

      <section className="section-shell mt-24">
        <SectionHeading
          eyebrow="How It Works"
          title="A connected path to better grades"
          copy="A simple journey from diagnosis to consistent improvement, with parent visibility built into the process."
        />
        <div className="mt-10 grid gap-4 md:grid-cols-4">
          {howItWorks.map((step) => (
            <article key={step.number} className="surface-panel card-lift p-6">
              <div className="grid h-16 w-16 place-items-center rounded-full bg-gradient-to-br from-navy-900 to-navy-700 text-2xl font-extrabold text-white shadow-neon">
                {step.number}
              </div>
              <h3 className="mt-5 font-display text-2xl font-extrabold text-ink">{step.title}</h3>
              <p className="mt-3 text-sm leading-7 text-slate-600">{step.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section-shell mt-24">
        <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="dark-shell p-8">
            <SectionHeading
              eyebrow="Live Classes - 1:1 and Small Group"
              title="Live Classes that still feel personal"
              copy="Interactive sessions with experienced tutors, flexible timings, and revision-ready recordings."
              invert
            />
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {liveClassHighlights.map((item) => (
                <div key={item} className="glow-card px-4 py-4 text-sm font-semibold text-white/82">
                  {item}
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/#book-demo" className="button-primary">
                Book Free Trial
              </Link>
              <Link to="/marketplace" className="button-ghost">
                View Schedule
              </Link>
            </div>
          </div>

          <div className="space-y-5">
            <article className="surface-panel p-6">
              <p className="eyebrow-light">Upcoming Live Classes</p>
              <p className="mt-3 text-sm text-slate-600">Timezone: IST</p>
              <div className="mt-6 space-y-4">
                {liveClasses.map((item) => (
                  <div key={item.title} className="soft-panel p-5">
                    <p className="font-display text-2xl font-extrabold text-ink">Class: {item.title}</p>
                    <p className="mt-2 text-sm text-slate-600">{item.schedule}</p>
                    <p className="mt-1 text-sm text-slate-500">
                      Tutor: {item.tutor} | Level: {item.level}
                    </p>
                    <div className="mt-4 flex gap-3">
                      <Link to="/#book-demo" className="button-primary">
                        Book
                      </Link>
                      <Link to="/#contact" className="button-secondary">
                        Get Reminder
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </article>

            <article className="surface-panel p-6">
              <div className="flex items-center gap-4">
                <div className="grid h-16 w-16 place-items-center rounded-[22px] bg-gradient-to-br from-navy-900 to-slateblue text-3xl text-white shadow-neon">
                  PV
                </div>
                <div>
                  <p className="text-sm font-extrabold uppercase tracking-[0.22em] text-skyglow">
                    Priya Verma
                  </p>
                  <h3 className="mt-1 font-display text-2xl font-extrabold text-ink">
                    Maths Specialist | 8+ years teaching experience
                  </h3>
                </div>
              </div>
              <p className="mt-4 text-sm leading-7 text-slate-600">
                Focused on board exam success and JEE Advanced. Small batches, personalised
                homework, regular progress reports, and monthly mock reviews.
              </p>
              <div className="mt-5 flex gap-3">
                <Link to="/tutor/priya-verma" className="button-secondary">
                  View Profile
                </Link>
                <Link to="/tutor/priya-verma?book=1" className="button-primary">
                  Book Trial
                </Link>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section id="success-stories" className="section-shell mt-24">
        <SectionHeading
          eyebrow="Success Stories"
          title="Real students, real improvements"
          copy="Focused practice, mock reviews, and structured tutor support helped families move from anxiety to clarity."
        />
        <div className="mt-10 grid gap-5 lg:grid-cols-4">
          {successStories.map((story) => (
            <article key={story.name} className="surface-panel card-lift p-6">
              <div className="info-pill">{story.name}</div>
              <p className="mt-4 text-sm leading-7 text-slate-600">{story.line}</p>
              <p className="mt-5 text-sm font-extrabold text-signal">{story.meta}</p>
              <p className="mt-1 text-xs font-bold uppercase tracking-[0.22em] text-slate-400">
                {story.track}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="section-shell mt-24">
        <SectionHeading
          eyebrow="Frequently Asked Questions"
          title="Frequently Asked Questions"
          copy="Everything you need to know about demos, classes, mocks, recordings, and billing."
        />
        <div className="mt-10">
          <FAQAccordion items={faqItems} />
        </div>
      </section>

      <section id="contact" className="section-shell mt-24">
        <div className="dark-shell grid gap-8 p-8 lg:grid-cols-[0.92fr_1.08fr] lg:p-10">
          <div>
            <SectionHeading
              eyebrow="Contact"
              title="Book Demo"
              copy="Dreameer keeps the first touch simple: call, WhatsApp, or submit a quick demo request and let our academic team take care of the rest."
              invert
            />
            <div className="mt-8 space-y-4">
              <div className="glow-card p-4">
                <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-white/45">WhatsApp</p>
                <p className="mt-2 text-lg font-bold text-white">{siteInfo.phone}</p>
              </div>
              <div className="glow-card p-4">
                <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-white/45">Email</p>
                <p className="mt-2 text-lg font-bold text-white">{siteInfo.email}</p>
              </div>
              <div className="glow-card p-4">
                <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-white/45">Office Hours</p>
                <p className="mt-2 text-lg font-bold text-white">{siteInfo.officeHours}</p>
              </div>
            </div>
          </div>
          <BookingForm contextLabel="contact-section" variant="dark" />
        </div>
      </section>
    </div>
  );
}
